import { Component, Event, EventEmitter, Fragment, h, Listen, Method, Prop, State } from '@stencil/core';
import { VALIDATION_ATTRIBUTES } from '../../vs-form-builder.model';
import { checkValidations, getValidStateAccordingToRequiredStatus } from '../../helpers/validator.helper';
import { FileUploadAttr } from '@src/components/vs-form-builder/components/file-upload/vs-file-upload.model';
import {
  ALLOWED_FILE_TYPES,
  FILE_SIZE_LIMIT, FILE_TYPE_MAP, LOADING_GIF,
} from '@src/components/vs-form-builder/components/file-upload/vs-file-upload.helper';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';


@Component({
  tag: 'vs-file-upload',
  shadow: false,
})

export class VsFileUpload {

  /**
   * @shortDescription Identifier of the file upload field.
   * @controlName inputId */
  @Prop() inputId: string;

  /**
   * @shortDescription Index of file upload field in a form.
   * @controlName index */
  @Prop() index: number;

  /**
   * @shortDescription Define the label for file upload field.
   * @controlName label */
  @Prop() label: string;

  /**
   * @shortDescription Accepted file types
   * @controlName accepts */
  @Prop() accepts: string = ALLOWED_FILE_TYPES;

  /**
   * @shortDescription Validators rules to check against for the file upload.
   * @controlName validators */
  @Prop() validators: any[] = [];

  /**
   * @shortDescription Tooltip text for the input field.
   * @controlName tooltip */
  @Prop() tooltip: string;

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /** @shortDescription error message when file is with wrong format
   * @controlName fileFormatErrorMessage
   */
  @Prop() fileFormatErrorMessage: string;

  /** @shortDescription error message when the file is with the wrong size
   * @controlName fileSizeErrorMessage
   */
  @Prop() fileSizeErrorMessage: string;

  /** @shortDescription success message when the file is uploaded successfully
   * @controlName successUploadMessage
   */
  @Prop() successUploadMessage: string;

  /** @shortDescription message when the file is uploading
   * @controlName uploadingMessage
   */
  @Prop() uploadingMessage: string;

  /** @shortDescription error message when the file is not uploaded
   * @controlName uploadErrorMessage
   */
  @Prop() uploadErrorMessage: string;


  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription HTML content to be inserted before the form field
   * @controlName aboveInputContent
   */
  @Prop() aboveInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName belowInputContent
   */
  @Prop() belowInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName betweenInputContent
   */
  @Prop() betweenInputContent: string;
  /* END GENAI@GHCOPILOT */

  @State() showError: boolean = false;
  @State() value: string = '';
  @State() selectedFileErrorMessage: string = '';
  @State() valid: boolean = true;
  @State() inputAllyAlert: string = 'file_upload_alert_';
  @State() isRequired: boolean;
  @State() isUploadingInProgress: boolean = false;
  @State() isSuccessFileUpload: boolean = false;
  @State() fileTypesToMatch = [];
  @State() finalFileTooltipMessage: string = '';
  /**
   * @shortDescription Emit file upload blur event to parent component on input blur
   * @controlName blurEvent */
  @Event() blurEvent: EventEmitter<void>;

  private inputEl?;

  /**
   * @shortDescription Emit file upload value on change
   * @controlName computedInputValue */
  @Event({
    eventName: 'computedInputValue',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) computedInputValue: EventEmitter;

  /**
   * @shortDescription Reset value when reset button is clicked
   * @controlName resetValue */
  @Method()
  async resetValue(isShowError = false) {
    this.showError = isShowError;
    this.valid = true;
    this.value = null;
    this.updateInput(this.value);
    this.isUploadingInProgress = false;
    this.isSuccessFileUpload = false;
  }


  @Listen('uploadFileInProgressEvent', { target: 'body' })
  uploadingInProgress(uploadFileInProgressEvent) {
    this.isUploadingInProgress = uploadFileInProgressEvent?.detail?.isUploading;
    this.isSuccessFileUpload = uploadFileInProgressEvent?.detail?.status;
  }

  /**
   * @shortDescription set file upload focus
   * @controlName setFocus */
  @Method()
  async setFocus() {
    this.showError = true;
    this.validateFile(this.value);
    this.inputEl.focus();
  }

  // START GENAI@CHATGPT4
  componentWillLoad() {
    this.checkIfRequired();
    this.formatAcceptedFileTypes();
    this.updateFileTooltipMessage();
    this.updateInputId();
    this.updateInput(null);
  }

  updateInputId() {
    this.inputAllyAlert += this.inputId;
  }

  checkIfRequired() {
    this.isRequired = this.validators.findIndex(validator => validator.key === VALIDATION_ATTRIBUTES.REQUIRE) > -1;
  }

  formatAcceptedFileTypes() {
    if (this.accepts) {
      this.fileTypesToMatch = this.accepts.replace(/\./g, '')
        .toLowerCase().split(',').map(type => type.trim());
    }
  }

  updateFileTooltipMessage() {
    if (this.tooltip) {
      this.finalFileTooltipMessage = this.tooltip;
    } else {
      this.finalFileTooltipMessage = this.generateFileTooltipMessage();
    }
  }

  generateFileTooltipMessage() {
    const fileTypeMessage = this.fileTypesToMatch?.map(fileType => {
      return FILE_TYPE_MAP[fileType];
    }).join(', ');
    return `Please upload file to continue. File type should be ${fileTypeMessage}. File size should be less than 8 MB.`;
  }

// END GENAI@CHATGPT4


  private validateFile = (file) => {
    const validationAttr = checkValidations(this.validators, file);
    this.selectedFileErrorMessage = validationAttr.errorMessage;
    try {
      this.checkFileSize(file.size);
    } catch (e) {
      if (!this.selectedFileErrorMessage) {
        this.selectedFileErrorMessage = this.fileSizeErrorMessage || e.message;
      }
      validationAttr.valid = false;
    }
    try {
      const fileType = file.name.split('.').pop();
      this.checkFileType(fileType);
    } catch (e) {
      if (!this.selectedFileErrorMessage) {
        this.selectedFileErrorMessage = this.fileFormatErrorMessage || e.message;
      }
      validationAttr.valid = false;
    }

    this.valid = getValidStateAccordingToRequiredStatus(this.isRequired, file, validationAttr);

    return this.valid;
  };

  private checkFileSize = (value) => {
    if (value > FILE_SIZE_LIMIT) {
      throw new Error(`File size exceeds ${FILE_SIZE_LIMIT / 1024}MB`);
    }
    return true;
  };

  private checkFileType = (value) => {
    if (this.fileTypesToMatch?.includes(value)) {
      return true;
    }
    throw new Error(`Invalid file type. Supported file types are ${this.accepts}`);
  };


  private onFileUploadChange = (event) => {
    const file = event?.target?.files[0];
    this.showError = true;
    this.validateFile(file);
    this.updateInput(file);
  };

  private updateInput = (value) => {
    this.value = value;
    this.validateFile(this.value);
    this.computedInputValue.emit(
      {
        index: this.index,
        key: this.inputId,
        value: this.value,
        isValid: this.valid,
        error: this.selectedFileErrorMessage,
      } as FileUploadAttr,
    );
  };

  render() {
    return (
      <Fragment>
        {this.aboveInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.aboveInputContent} />}
        <div class={`vs-input-group vs-height-auto vs-mb-1`}>
          {this.label && (
            <Fragment>
              <label class={`vs-label vs-mr-1`}
                     htmlFor={this.inputId}>
                <span innerHTML={this.label} />
                {this.isRequired ? <strong class='required'><i> {this.requiredText} </i></strong> : ''}
              </label>
              {<vs-tooltip
                ally-open-label={`Open tooltip for ${this.label}`}
                tooltip-id={`tooltip${this.inputId}`}
                tooltip-header={this.label}
                tooltip-body={this.finalFileTooltipMessage} />
              }
            </Fragment>
          )}
          {this.betweenInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.betweenInputContent} />}
          <input
            ref={el => this.inputEl = el as HTMLInputElement}
            class={`vs-input ${this.showError && !this.valid ? 'vs-error' : ''}`}
            id={this.inputId}
            name={this.inputId}
            type='file'
            accept={this.accepts}
            value={this.value}
            onInput={this.onFileUploadChange}
            aria-invalid={this.showError && !this.valid}
            aria-describedby={this.showError && !this.valid ? this.inputAllyAlert + this.inputId : ''}
          />
          {this.showError && (this.isUploadingInProgress ?
              (<p role='alert'  class='vs-text vs-pt-1 vs-m-0'>
                <img style={{ width: '25px' }} src={LOADING_GIF} alt='loading' />{this.uploadingMessage}
              </p>) :
              (this.isSuccessFileUpload &&
                <p role='alert' aria-live='polite' class='vs-text vs-pb-1'>{this.successUploadMessage}</p>
              )
          )}
          {this.showError && !this.valid && this.selectedFileErrorMessage && (
            <p role='alert' aria-live='polite' class='vs-meta vs-text vs-alert vs-inline-alert vs-pb-1'
               id={this.inputAllyAlert + this.inputId}>
              <span aria-hidden='true' class='visa-icon-alert' />
              <span innerHTML={decodeHTMLEntities(this.selectedFileErrorMessage)} />
            </p>
          )}
        </div>
        {this.belowInputContent &&  <div class="vs-text vs-mb-0" innerHTML={this.belowInputContent} />}
      </Fragment>
    );
  };
}
